import {
  AcceptUserAgreementsError,
  Mutation,
  MutationAcceptUserAgreementsArgs,
  SubmitV2APaymentResultError,
  SubmitV2APaymentResultSuccess,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  getSubmitVariables,
  SubmitV2ATransferVariables,
  V2APaymentData,
} from 'features/v2a/sender/helpers/Sender.helpers';
import { useCallback } from 'react';
import AcceptUserAgreements from 'services/graphql/mutations/MoveMoney/AcceptUserAgreements';
import SubmitV2ATransfer from 'services/graphql/mutations/MoveMoney/SubmitV2ATransfer';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import { useIdempotencyToken } from 'shared/utils/idempotencyTokens/idempotencyTokenUtils';

export type V2ASubmitTransferOutcome =
  | {
      status: 'success';
      data: SubmitV2APaymentResultSuccess;
      amount: string;
    }
  | {
      status: 'failure';
      stage: 'agreement' | 'transfer';
      title?: string;
      message?: string;
      code?: string;
    }
  | {
      status: 'cancelled';
    };

const getAgreementFailure = (
  error?: AcceptUserAgreementsError,
): V2ASubmitTransferOutcome => ({
  status: 'failure',
  stage: 'agreement',
  title: error?.title,
  message: error?.message,
  code: error?.code,
});

const getTransferFailure = (
  error?: SubmitV2APaymentResultError,
): V2ASubmitTransferOutcome => ({
  status: 'failure',
  stage: 'transfer',
  title: error?.title,
  message: error?.message,
  code: error?.code,
});

const useV2ASubmitTransfer = () => {
  const [submitTransferData, executeSubmitTransfer] = useCancellableMutation<
    Pick<Mutation, 'submitV2APayment'>,
    SubmitV2ATransferVariables
  >(SubmitV2ATransfer);

  const [agreementData, acceptAgreement] = useCancellableMutation<
    Pick<Mutation, 'acceptUserAgreements'>,
    MutationAcceptUserAgreementsArgs
  >(AcceptUserAgreements);

  const { idempotencyToken } = useIdempotencyToken('0');

  const submitTransfer = useCallback(
    async (
      paymentData: V2APaymentData,
      displayName: string,
      transferAgreementId?: string,
    ): Promise<V2ASubmitTransferOutcome> => {
      const confirmedAmount = paymentData.amount;
      const submitVariables = getSubmitVariables(
        paymentData,
        displayName,
        idempotencyToken,
      );

      try {
        if (transferAgreementId) {
          const agreementResponse = await acceptAgreement({
            input: { agreementIds: [transferAgreementId] },
          });

          if (agreementResponse.error) {
            return {
              status: 'failure',
              stage: 'agreement',
              message: agreementResponse.error.message,
            };
          }

          const agreementResult = agreementResponse.data?.acceptUserAgreements;
          if (
            agreementResult?.__typename === Typenames.AcceptUserAgreementsError
          ) {
            return getAgreementFailure(agreementResult);
          }

          if (
            agreementResult?.__typename !==
              Typenames.AcceptUserAgreementsSuccess ||
            !agreementResult.accepted
          ) {
            return getAgreementFailure();
          }
        }

        const transferResponse = await executeSubmitTransfer(submitVariables);

        if (transferResponse.error) {
          return {
            status: 'failure',
            stage: 'transfer',
            message: transferResponse.error.message,
          };
        }

        const transferResult = transferResponse.data?.submitV2APayment;
        if (
          transferResult?.__typename === Typenames.SubmitV2APaymentResultError
        ) {
          return getTransferFailure(transferResult);
        }

        if (
          transferResult?.__typename !== Typenames.SubmitV2APaymentResultSuccess
        ) {
          return getTransferFailure();
        }

        return {
          status: 'success',
          data: transferResult,
          amount: confirmedAmount,
        };
      } catch (error) {
        if (error instanceof AbortError) {
          return { status: 'cancelled' };
        }

        throw error;
      }
    },
    [acceptAgreement, executeSubmitTransfer, idempotencyToken],
  );

  return {
    submitTransfer,
    fetching: agreementData.fetching || submitTransferData.fetching,
  };
};

export default useV2ASubmitTransfer;
